import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { siteClientsFetchDataSelector } from 'features/networkAi/selectors'
import { fetchSiteClientsThunk } from 'features/networkAi/slice'
import { SiteClients } from '../types'
import { SpinnerCentered } from 'components/Loader'
import ClientLineItem from './ClientLineItem'
import {
  NetworkClientListFrame,
  NetworkClientListContentFrame,
  NetworkClientListInfoFrame,
  NetworkClientListListFrame,
  NetworkClientListInnerBox,
  NetworkClientListBoxHeaderInner,
  NetworkClientListBoxDetails,
  NetworkClientListClientListHeader,
  NetworkClientListHeaderTextFrameName,
  NetworkClientListHeaderTextFrameMac,
  NetworkClientListHeaderTextFrameIP,
  NetworkClientListHeaderTextFrameDevice,
  NetworkClientListHeaderTextFrameProtocol,
  NetworkClientListHeaderTextFrameBand,
  NetworkClientListHeaderTextFrameSSID,
  NetworkClientListHeaderTextFrameBytes,
  NetworkClientListHeaderTextFrameConnected,
  NetworkClientListBoxHeaderFont,
  NetworkClientListBoxDetailsFont,
  NetworkClientListHeaderText,
} from './style'

interface ClientsListViewProps {
  siteId: string
}

function ClientsListView({ siteId }: ClientsListViewProps) {
  const dispatch = useAppDispatch()
  const siteClients = useAppSelector(siteClientsFetchDataSelector.data)
  const isLoading = useAppSelector(siteClientsFetchDataSelector.isFetching)

  useEffect(() => {
    siteId && dispatch(fetchSiteClientsThunk(siteId))
  }, [dispatch, siteId])

  const siteClientsSafe: SiteClients[] = Array.isArray(siteClients)
    ? siteClients
    : [{ mac: ' ' } as SiteClients]

  function getBandsCount(metric: string) {
    let count = 0
    siteClientsSafe.forEach((item) => {
      if (item.band === metric) {
        count += 1
      }
    })
    return count
  }

  function getProtoCount(metric: string) {
    let count = 0
    siteClientsSafe.forEach((item) => {
      if (item.proto === metric) {
        count += 1
      }
    })
    return count
  }

  return (
    <>
      {isLoading ? (
        <SpinnerCentered mt="200px" />
      ) : (
        <NetworkClientListFrame>
          <NetworkClientListContentFrame>
            <NetworkClientListInfoFrame>
              <NetworkClientListInnerBox>
                <NetworkClientListBoxHeaderInner>
                  <NetworkClientListBoxHeaderFont>
                    Total Clients
                  </NetworkClientListBoxHeaderFont>
                </NetworkClientListBoxHeaderInner>
                <NetworkClientListBoxDetails>
                  <NetworkClientListBoxDetailsFont>
                    {siteClientsSafe.length}
                  </NetworkClientListBoxDetailsFont>
                </NetworkClientListBoxDetails>
              </NetworkClientListInnerBox>

              <NetworkClientListInnerBox>
                <NetworkClientListBoxHeaderInner>
                  <NetworkClientListBoxHeaderFont>
                    2.4 GHz
                  </NetworkClientListBoxHeaderFont>
                </NetworkClientListBoxHeaderInner>
                <NetworkClientListBoxDetails>
                  <NetworkClientListBoxDetailsFont>
                    {getBandsCount('24')}
                  </NetworkClientListBoxDetailsFont>
                </NetworkClientListBoxDetails>
              </NetworkClientListInnerBox>

              <NetworkClientListInnerBox>
                <NetworkClientListBoxHeaderInner>
                  <NetworkClientListBoxHeaderFont>
                    5 GHz
                  </NetworkClientListBoxHeaderFont>
                </NetworkClientListBoxHeaderInner>
                <NetworkClientListBoxDetails>
                  <NetworkClientListBoxDetailsFont>
                    {getBandsCount('5')}
                  </NetworkClientListBoxDetailsFont>
                </NetworkClientListBoxDetails>
              </NetworkClientListInnerBox>

              <NetworkClientListInnerBox>
                <NetworkClientListBoxHeaderInner>
                  <NetworkClientListBoxHeaderFont>
                    6 GHz
                  </NetworkClientListBoxHeaderFont>
                </NetworkClientListBoxHeaderInner>
                <NetworkClientListBoxDetails>
                  <NetworkClientListBoxDetailsFont>
                    {getBandsCount('6')}
                  </NetworkClientListBoxDetailsFont>
                </NetworkClientListBoxDetails>
              </NetworkClientListInnerBox>

              <NetworkClientListInnerBox>
                <NetworkClientListBoxHeaderInner>
                  <NetworkClientListBoxHeaderFont>
                    802.11ax
                  </NetworkClientListBoxHeaderFont>
                </NetworkClientListBoxHeaderInner>
                <NetworkClientListBoxDetails>
                  <NetworkClientListBoxDetailsFont>
                    {getProtoCount('ax')}
                  </NetworkClientListBoxDetailsFont>
                </NetworkClientListBoxDetails>
              </NetworkClientListInnerBox>

              <NetworkClientListInnerBox>
                <NetworkClientListBoxHeaderInner>
                  <NetworkClientListBoxHeaderFont>
                    802.11ac
                  </NetworkClientListBoxHeaderFont>
                </NetworkClientListBoxHeaderInner>
                <NetworkClientListBoxDetails>
                  <NetworkClientListBoxDetailsFont>
                    {getProtoCount('ac')}
                  </NetworkClientListBoxDetailsFont>
                </NetworkClientListBoxDetails>
              </NetworkClientListInnerBox>

              <NetworkClientListInnerBox>
                <NetworkClientListBoxHeaderInner>
                  <NetworkClientListBoxHeaderFont>
                    802.11n
                  </NetworkClientListBoxHeaderFont>
                </NetworkClientListBoxHeaderInner>
                <NetworkClientListBoxDetails>
                  <NetworkClientListBoxDetailsFont>
                    {getProtoCount('n')}
                  </NetworkClientListBoxDetailsFont>
                </NetworkClientListBoxDetails>
              </NetworkClientListInnerBox>

              <NetworkClientListInnerBox>
                <NetworkClientListBoxHeaderInner>
                  <NetworkClientListBoxHeaderFont>
                    802.11g
                  </NetworkClientListBoxHeaderFont>
                </NetworkClientListBoxHeaderInner>
                <NetworkClientListBoxDetails>
                  <NetworkClientListBoxDetailsFont>
                    {getProtoCount('g')}
                  </NetworkClientListBoxDetailsFont>
                </NetworkClientListBoxDetails>
              </NetworkClientListInnerBox>
            </NetworkClientListInfoFrame>

            <NetworkClientListListFrame>
              <NetworkClientListClientListHeader>
                <NetworkClientListHeaderTextFrameName>
                  <NetworkClientListHeaderText>
                    <b>Name</b>
                  </NetworkClientListHeaderText>
                </NetworkClientListHeaderTextFrameName>

                <NetworkClientListHeaderTextFrameMac>
                  <NetworkClientListHeaderText>
                    MAC Address
                  </NetworkClientListHeaderText>
                </NetworkClientListHeaderTextFrameMac>

                <NetworkClientListHeaderTextFrameIP>
                  <NetworkClientListHeaderText>
                    IPv4
                  </NetworkClientListHeaderText>
                </NetworkClientListHeaderTextFrameIP>

                <NetworkClientListHeaderTextFrameDevice>
                  <NetworkClientListHeaderText>
                    Device Type
                  </NetworkClientListHeaderText>
                </NetworkClientListHeaderTextFrameDevice>

                <NetworkClientListHeaderTextFrameProtocol>
                  <NetworkClientListHeaderText>
                    Protocol
                  </NetworkClientListHeaderText>
                </NetworkClientListHeaderTextFrameProtocol>

                <NetworkClientListHeaderTextFrameBand>
                  <NetworkClientListHeaderText>
                    Band
                  </NetworkClientListHeaderText>
                </NetworkClientListHeaderTextFrameBand>

                <NetworkClientListHeaderTextFrameSSID>
                  <NetworkClientListHeaderText>
                    SSID
                  </NetworkClientListHeaderText>
                </NetworkClientListHeaderTextFrameSSID>

                <NetworkClientListHeaderTextFrameBytes>
                  <NetworkClientListHeaderText>
                    Bytes
                  </NetworkClientListHeaderText>
                </NetworkClientListHeaderTextFrameBytes>

                <NetworkClientListHeaderTextFrameConnected>
                  <NetworkClientListHeaderText>
                    Connected
                  </NetworkClientListHeaderText>
                </NetworkClientListHeaderTextFrameConnected>
              </NetworkClientListClientListHeader>

              {siteClientsSafe.map((item) => (
                <ClientLineItem
                  key={item.mac}
                  siteClient={item}
                ></ClientLineItem>
              ))}
            </NetworkClientListListFrame>
          </NetworkClientListContentFrame>
        </NetworkClientListFrame>
      )}
    </>
  )
}

export default ClientsListView
